'use client';

import { useRef } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import type { SystemStyleObject } from '@/styled-system/types';
import { rangeToggleGroupFluidStyles, rangeToggleGroupStyles } from './rangeToggleGroup.styles';

export type RangeToggleGroupOption<T extends string | number> = {
  value: T;
  label: string;
};

export type RangeToggleGroupProps<T extends string | number> = {
  options: Array<RangeToggleGroupOption<T>>;
  minValue: T | null;
  maxValue: T | null;
  onChange: (minValue: T | null, maxValue: T | null) => void;
  'aria-label': string;
  /** When provided, clicking the option with this value always calls onChange(null, null). */
  clearValue?: T;
  size?: 'sm' | 'md' | 'lg';
  /** Expand buttons to fill all available horizontal space. Mirrors ButtonGroup's fluid prop. */
  fluid?: boolean;
  css?: SystemStyleObject;
  className?: string;
};

/**
 * Pure range-selection logic. Exported for unit testing.
 *
 * Endpoint clicks contract the range — clicking the current min moves it one
 * step inward; clicking the current max moves it one step inward. This lets
 * users narrow an existing range without resetting via "Any".
 *
 * All other clicks: extend the range (outside) or move the nearest thumb
 * (inside, ties go to min).
 *
 * Repeating the same option click selects exactly that value, matching the
 * bedroom-filter interaction where two clicks on "2" select only 2 beds.
 */
export function computeRange<T extends string | number>(
  clickedIndex: number,
  options: Array<RangeToggleGroupOption<T>>,
  minValue: T | null,
  maxValue: T | null,
  clearValue?: T,
  previousClickedValue?: T,
): [T | null, T | null] {
  const clicked = options[clickedIndex];

  if (clearValue !== undefined && clicked.value === clearValue) {
    return [null, null];
  }

  const minIdx = minValue !== null ? options.findIndex((o) => o.value === minValue) : -1;
  const maxIdx = maxValue !== null ? options.findIndex((o) => o.value === maxValue) : -1;

  // Nothing selected: anchor both thumbs at the clicked position.
  if (minIdx === -1) {
    return [clicked.value, clicked.value];
  }

  const isRepeatedClick = previousClickedValue !== undefined && clicked.value === previousClickedValue;
  if (isRepeatedClick) {
    return [clicked.value, clicked.value];
  }

  // Clicking the sole selected option keeps that exact value selected.
  if (minIdx === maxIdx && clickedIndex === minIdx) {
    return [clicked.value, clicked.value];
  }

  // Clicking the current min (on a range) contracts from the left.
  if (clickedIndex === minIdx && minIdx < maxIdx) {
    return [options[minIdx + 1].value, maxValue];
  }

  // Clicking the current max (on a range) contracts from the right.
  if (clickedIndex === maxIdx && minIdx < maxIdx) {
    return [minValue, options[maxIdx - 1].value];
  }

  // Extend leftward
  if (clickedIndex < minIdx) {
    return [clicked.value, maxValue];
  }

  // Extend rightward
  if (clickedIndex > maxIdx) {
    return [minValue, clicked.value];
  }

  // Inside the range (not at an endpoint): move the nearest thumb (ties → min).
  const distToMin = clickedIndex - minIdx;
  const distToMax = maxIdx - clickedIndex;
  return distToMin <= distToMax ? [clicked.value, maxValue] : [minValue, clicked.value];
}

export default function RangeToggleGroup<T extends string | number>({
  options,
  minValue,
  maxValue,
  onChange,
  'aria-label': ariaLabel,
  clearValue,
  size = 'sm',
  fluid = false,
  css: cssProp,
  className,
}: RangeToggleGroupProps<T>) {
  const minIdx = minValue !== null ? options.findIndex((o) => o.value === minValue) : -1;
  const maxIdx = maxValue !== null ? options.findIndex((o) => o.value === maxValue) : -1;
  const previousClickedValueRef = useRef<T | undefined>(undefined);

  return (
    <ToggleButtonGroup
      aria-label={ariaLabel}
      size={size}
      css={css.raw(rangeToggleGroupStyles, fluid && rangeToggleGroupFluidStyles, cssProp)}
      className={className}
    >
      {options.map((option, index) => {
        const isClearOption = clearValue !== undefined && option.value === clearValue;
        const isInRange = !isClearOption && minIdx !== -1 && index >= minIdx && index <= maxIdx;
        const isClearActive = isClearOption && minIdx === -1;

        return (
          <ToggleButton
            key={String(option.value)}
            value={String(option.value)}
            selected={isInRange || isClearActive}
            onClick={() => {
              const [nextMin, nextMax] = computeRange(
                index,
                options,
                minValue,
                maxValue,
                clearValue,
                previousClickedValueRef.current,
              );
              previousClickedValueRef.current = option.value;
              onChange(nextMin, nextMax);
            }}
          >
            {option.label}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
